import { Controller, UseFormReturn } from "react-hook-form";
import Applicants from "@/model/interface/form/Applicants";
import ApiApplicationApplicant from "@/model/interface/api/ApiApplicationApplicant";
import React from "react";
import { Pencil, ChevronDown } from "lucide-react";
import CustomSelect from "@/components/shared/CustomSelect";
import { EmploymentStatus } from "@/model/interface/EmploymentStatus";
import DatePicker from "react-datepicker";
import { EmploymentType } from "@/model/interface/EmploymentType";
import { daysDifferenceBetweenDates } from "@/util/date";

const inputClass = (hasError: boolean) =>
  `w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${hasError ? "border-red-400" : "border-gray-300"}`;

const ApplicantCurrentMortgage = ({
  form,
  applicant,
  applicantType,
}: {
  form: UseFormReturn<Applicants>;
  applicant?: ApiApplicationApplicant;
  applicantType: "mainApplicant" | "coApplicant";
}) => {
  const { control, watch, register, formState } = form;
  const employmentStatus = watch(`${applicantType}.employmentStatus`);
  const employmentDate = watch(`${applicantType}.currentJobStartDate`);

  const currentDate = new Date();
  const threeYearsInDays = 3 * 365;
  const showPreviousEmployment =
    employmentDate && daysDifferenceBetweenDates(new Date(employmentDate), currentDate) < threeYearsInDays;

  return (
    <div className="space-y-5">
      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Employment status</h6>
        <Controller
          control={control}
          name={`${applicantType}.employmentStatus`}
          render={({ field }) => (
            <CustomSelect
              value={EmploymentStatus.find((ms) => ms.value === field.value)!}
              onChange={(o: EmploymentStatus) => field.onChange(o.value)}
              options={EmploymentStatus}
              mapOptionToLabel={(o: EmploymentStatus) => o.label}
              mapOptionToValue={(o: EmploymentStatus) => o.value}
            />
          )}
        />
        <ChevronDown className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors[applicantType]?.employmentStatus && (
          <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.employmentStatus?.message}</p>
        )}
      </div>
      {employmentStatus === "employed" && (
        <>
          <div className="relative">
            <h6 className="text-sm text-gray-500 mb-1">How long have you been employed at your current job?</h6>
            <Controller
              control={control}
              name={`${applicantType}.currentJobStartDate`}
              render={({ field }) => (
                <DatePicker
                  wrapperClassName="block w-full"
                  className={inputClass(!!formState.errors[applicantType]?.currentJobStartDate)}
                  placeholderText="Select approximate date"
                  onChange={(date) => field.onChange(date)}
                  selected={field.value}
                />
              )}
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {formState.errors[applicantType]?.currentJobStartDate && (
              <p className="mt-1 text-sm text-red-500">
                {formState.errors[applicantType]?.currentJobStartDate?.message}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="relative">
              <h6 className="text-sm text-gray-500 mb-1">Employer name</h6>
              <input
                type="text"
                {...register(`${applicantType}.employerName`, { value: applicant?.employerName })}
                className={inputClass(!!formState.errors[applicantType]?.employerName)}
                placeholder="Employer name"
              />
              <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
              {formState.errors[applicantType]?.employerName && (
                <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.employerName?.message}</p>
              )}
            </div>
            <div className="relative">
              <h6 className="text-sm text-gray-500 mb-1">Employment type</h6>
              <Controller
                control={control}
                name={`${applicantType}.employmentType`}
                render={({ field }) => (
                  <CustomSelect
                    value={EmploymentType.find((ms) => ms.value === field.value)!}
                    onChange={(o: EmploymentType) => field.onChange(o.value)}
                    options={EmploymentType}
                    mapOptionToLabel={(o: EmploymentType) => o.label}
                    mapOptionToValue={(o: EmploymentType) => o.value}
                  />
                )}
              />
              <ChevronDown className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
              {formState.errors[applicantType]?.employmentType && (
                <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.employmentType?.message}</p>
              )}
            </div>
          </div>
          <div className="relative">
            <h6 className="text-sm text-gray-500 mb-1">Company address</h6>
            <input
              type="text"
              {...register(`${applicantType}.companyAddress`, { value: applicant?.companyAddress })}
              className={inputClass(!!formState.errors[applicantType]?.companyAddress)}
              placeholder="Company address"
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {formState.errors[applicantType]?.companyAddress && (
              <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.companyAddress?.message}</p>
            )}
          </div>
        </>
      )}
      {showPreviousEmployment && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="relative">
            <h6 className="text-sm text-gray-500 mb-1">Previous employer name</h6>
            <input
              type="text"
              {...register(`${applicantType}.previousEmployer`, { value: applicant?.previousEmployer })}
              className={inputClass(!!formState.errors[applicantType]?.previousEmployer)}
              placeholder="Employer name"
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {formState.errors[applicantType]?.previousEmployer && (
              <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.previousEmployer?.message}</p>
            )}
          </div>
          <div className="relative">
            <h6 className="text-sm text-gray-500 mb-1">Previous job start date</h6>
            <Controller
              control={control}
              name={`${applicantType}.previousJobStartDate`}
              render={({ field }) => (
                <DatePicker
                  wrapperClassName="block w-full"
                  className={inputClass(!!formState.errors[applicantType]?.previousJobStartDate)}
                  placeholderText="Select approximate date"
                  onChange={(date) => field.onChange(date)}
                  selected={field.value}
                />
              )}
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {formState.errors[applicantType]?.previousJobStartDate && (
              <p className="mt-1 text-sm text-red-500">
                {formState.errors[applicantType]?.previousJobStartDate?.message}
              </p>
            )}
          </div>
          <div className="relative">
            <h6 className="text-sm text-gray-500 mb-1">Previous job end date</h6>
            <Controller
              control={control}
              name={`${applicantType}.previousJobEndDate`}
              render={({ field }) => (
                <DatePicker
                  wrapperClassName="block w-full"
                  className={inputClass(!!formState.errors[applicantType]?.previousJobEndDate)}
                  placeholderText="Select approximate date"
                  onChange={(date) => field.onChange(date)}
                  selected={field.value}
                />
              )}
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {formState.errors[applicantType]?.previousJobEndDate && (
              <p className="mt-1 text-sm text-red-500">
                {formState.errors[applicantType]?.previousJobEndDate?.message}
              </p>
            )}
          </div>
        </div>
      )}
      {employmentStatus === "self-employed" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="relative">
            <h6 className="text-sm text-gray-500 mb-1">Accountant details</h6>
            <input
              type="text"
              {...register(`${applicantType}.accountantDetails`, { value: applicant?.accountantDetails })}
              className={inputClass(!!formState.errors[applicantType]?.accountantDetails)}
              placeholder="Accountant details"
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {formState.errors[applicantType]?.accountantDetails && (
              <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.accountantDetails?.message}</p>
            )}
          </div>
          <div className="relative">
            <h6 className="text-sm text-gray-500 mb-1">ABN number</h6>
            <input
              type="text"
              {...register(`${applicantType}.abnNumber`, { value: applicant?.abnNumber })}
              className={inputClass(!!formState.errors[applicantType]?.abnNumber)}
              placeholder="ABN number"
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {formState.errors[applicantType]?.abnNumber && (
              <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.abnNumber?.message}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicantCurrentMortgage;
